import type { Writable } from "@glideapps/ts-necessities";

import type { Icon } from "../../components/icons";
import type { SerializedComponent } from "../../components/SerializedApp";
import { AppKind } from "../../components/SerializedApp";
import type {
    ActionsRecord,
    AppDescriptionContext,
    ComponentBuilder,
    EditedColumnsAndTables,
    InteractiveComponentConfiguratorContext,
    RewritingComponentConfiguratorContext,
    SchemaInspector,
    WireActionHydrator,
    WireInflationBackend,
} from "../components";
import { PopulationMode } from "../components/description-handlers";
import { getEditedColumnsForProperties } from "../components/descriptor-utils";
import { populateDescription } from "../components/populate-description";
import {
    getNumberProperty,
    getSourceColumnProperty,
    getStringProperty,
    getTableProperty,
    isPropertyDescription,
} from "../components/property-utils";
import type {
    ActionDescription,
    ActionKind,
    InputOutputTables,
    MutatingScreenKind,
    TableColumn,
    LegacyPropertyDescription} from "../description";
import {
    getTableColumnDisplayName,
    sheetNameForTable
} from "../description";
import { getColumnForSourceColumn } from "../source-column";
import type { ActionDescriptor } from "./action-descriptor";
import type { ActionColumnAssignments, ActionHandler, DescriptionToken } from "./action-handler";

export abstract class BaseActionHandler<T extends ActionDescription> implements ActionHandler<T> {
    public abstract readonly kind: ActionKind;
    public abstract readonly iconName: Icon | true | undefined;

    public get needsData(): boolean {
        return false;
    }

    public get handlesSequencing(): boolean {
        return false;
    }

    public get appKinds(): AppKind | "both" {
        if (this.inflate !== undefined) {
            return "both";
        } else {
            return AppKind.App;
        }
    }

    public abstract getDescriptor(
        desc: T | undefined,
        tables: InputOutputTables | undefined,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ActionDescriptor | undefined;

    public getActions(_desc: T): ActionsRecord {
        return { actions: [] };
    }

    public getDescriptionToWalk(desc: T, _ccc: AppDescriptionContext): T {
        return desc;
    }

    public getScreensUsed(
        _desc: T,
        _adc: AppDescriptionContext,
        _tables: InputOutputTables | undefined
    ): readonly string[] {
        return [];
    }

    public abstract makeAction(
        builder: ComponentBuilder,
        desc: T,
        tables: InputOutputTables,
        screenContext: SerializedComponent | undefined,
        outputContext: SerializedComponent,
        mutatingScreenKind: MutatingScreenKind | undefined,
        detailScreenTitle: SerializedComponent | undefined,
        itemID: SerializedComponent | undefined,
        onSuccess: SerializedComponent | undefined,
        fromInlineList: boolean
    ): SerializedComponent | undefined;

    public newActionDescription(
        tables: InputOutputTables,
        iccc: InteractiveComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): T | undefined {
        const desc = { kind: this.kind } as T;
        return populateDescription(
            d => this.getDescriptor(d, tables, iccc, mutatingScreenKind)?.properties,
            PopulationMode.Default,
            desc,
            desc,
            tables,
            iccc,
            false,
            mutatingScreenKind,
            undefined,
            undefined,
            true
        );
    }

    public rewriteAfterReload(
        desc: T,
        tables: InputOutputTables,
        ccc: RewritingComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): T | undefined {
        return populateDescription(
            d => this.getDescriptor(d, tables, ccc, mutatingScreenKind)?.properties,
            PopulationMode.Rewrite,
            desc,
            desc,
            tables,
            ccc,
            true,
            mutatingScreenKind
        );
    }

    public getEditedColumns(
        desc: T,
        tables: InputOutputTables,
        ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): EditedColumnsAndTables | undefined {
        const descr = this.getDescriptor(desc, tables, ccc, mutatingScreenKind);
        if (descr === undefined) return undefined;
        return getEditedColumnsForProperties(descr.properties, desc, tables, mutatingScreenKind, ccc, true);
    }

    public getColumnAssignments(
        _ccc: AppDescriptionContext,
        _desc: T,
        _tables: InputOutputTables
    ): ActionColumnAssignments | undefined {
        return undefined;
    }

    public async duplicateAction(
        desc: T,
        _copyTables: InputOutputTables | undefined,
        _iccc: InteractiveComponentConfiguratorContext,
        _mutatingScreenKind: MutatingScreenKind | undefined,
        _screensCreated: Set<string>
    ): Promise<T | undefined> {
        return { ...desc };
    }

    public newDefaultActionDescription(_column: TableColumn): T | undefined {
        return undefined;
    }

    public fixActionDescription(_desc: Writable<T>): void {
        return undefined;
    }

    public getTokenizedDescription(
        _desc: T,
        _tables: InputOutputTables,
        _ccc: AppDescriptionContext
    ): readonly DescriptionToken[] | undefined {
        return undefined;
    }

    public inflate?(_ib: WireInflationBackend, _desc: T): WireActionHydrator | undefined;
}

export function tokenForProperty(
    p: LegacyPropertyDescription | undefined,
    tables: InputOutputTables | undefined,
    schema: SchemaInspector
): DescriptionToken | undefined {
    const stringProperty = getStringProperty(p);
    if (stringProperty !== undefined) {
        return {
            kind: "const",
            value: stringProperty,
        };
    }

    if (isPropertyDescription(p)) {
        const numProperty = getNumberProperty(p);
        if (numProperty !== undefined) {
            return {
                kind: "const",
                value: numProperty.toString(),
            };
        }
    }

    const tableName = getTableProperty(p);
    if (tableName !== undefined) {
        const table = schema.findTable(tableName);
        if (table === undefined) return undefined;
        return {
            kind: "column",
            value: sheetNameForTable(table),
        };
    }

    const sourceColumn = getSourceColumnProperty(p);
    if (sourceColumn === undefined) return undefined;

    const column = getColumnForSourceColumn(schema, sourceColumn, tables?.input, undefined);
    if (column === undefined) return undefined;

    return {
        kind: "column",
        value: getTableColumnDisplayName(column),
    };
}
